<script lang="ts">
    import { onMount } from 'svelte';
    import { createEventDispatcher } from 'svelte';
    import type { Image } from '@jet-app/music-classical/api/types';
    import { ContentKind } from '@jet-app/music-classical/models';
    import type { Artwork as ArtworkType } from '~/jet/models';
    import Artwork from '~/components/jet/Artwork/Artwork.svelte';

    interface ListPageItem {
        artwork: ArtworkType;
        image: Image;
        title: string;
        subtitle?: string;
        description?: string;
        type: ContentKind;
        heading?: string;
    }

    export let item: ListPageItem;
    export let isSelected: boolean = false;

    const dispatch = createEventDispatcher();
    const artworkProfile = 'product';
    const isEllipse = [ContentKind.Artist, ContentKind.Work].includes(
        item?.type,
    );

    // Max height of text container equivalent to three lines of text
    // when header, title, and subtitle text are all present
    const MAX_TEXT_HEIGHT_SHORT = 44;

    // Max height of text container equivalent to two lines of text
    // when header or subtitle text are NOT present
    const MAX_TEXT_HEIGHT_TALL = 46;

    let itemTextElement: HTMLElement;
    let itemTextHeight: number;

    $: truncateText =
        itemTextHeight > MAX_TEXT_HEIGHT_SHORT &&
        itemTextHeight !== MAX_TEXT_HEIGHT_TALL &&
        !truncateSubtitle;

    $: truncateSubtitle =
        !item?.heading && itemTextHeight >= MAX_TEXT_HEIGHT_TALL;

    onMount(() => {
        itemTextHeight = itemTextElement.clientHeight;
    });
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
    data-testid="list-page-item"
    class="list-page-item"
    class:is-selected={isSelected}
    aria-selected={isSelected}
    role="option"
    tabindex="0"
    on:click={() => dispatch('itemClicked', { item })}
>
    <div
        class="list-page-item-image"
        class:list-page-item-image__ellipse={isEllipse}
    >
        {#if item}
            <Artwork
                artwork={item?.artwork}
                profile={artworkProfile}
                fallbackKind={item.type}
                forceFullWidth={false}
            />
        {/if}
    </div>
    <div
        class="list-page-item-info"
        bind:this={itemTextElement}
        class:truncate-text={truncateText}
    >
        {#if item?.heading}
            <span
                class="list-page-item-heading"
                data-testid="list-page-item-heading"
            >
                {item.heading}
            </span>
        {/if}
        {#if item?.title}
            <span
                class="list-page-item-title"
                data-testid="list-page-item-title"
            >
                {item.title}
            </span>
        {/if}
        {#if item?.subtitle || item?.description}
            <span
                class="list-page-item-subtitle"
                data-testid="list-page-item-subtitle"
                class:truncate-subtitle={truncateSubtitle}
            >
                {item.subtitle || item.description}
            </span>
        {/if}
    </div>
</div>

<style lang="scss">
    @use 'amp/stylekit/core/mixins/line-clamp' as *;

    .list-page-item-image {
        height: 40px;
        width: 40px;
        min-width: 40px;
        border-radius: 5px;
        overflow: hidden;
    }

    .list-page-item-image__ellipse {
        border-radius: 20px;
    }

    .list-page-item {
        display: flex;
        gap: 0 10px;
        align-items: center;
        outline: none;
        height: 60px;
        padding: 12px;
    }

    .is-selected {
        background-color: var(--keyColor);
        border-radius: 10px;
    }

    .is-selected,
    .is-selected .list-page-item-subtitle,
    .is-selected .list-page-item-heading {
        color: white;
    }

    .list-page-item-info {
        display: flex;
        flex-direction: column;
    }

    .list-page-item-heading {
        text-transform: uppercase;
        color: var(--systemSecondary);
        font: var(--subhead-emphasized);

        @include line-clamp(1);
    }

    // Default line clamp for title & subtitle text
    .list-page-item-title,
    .list-page-item-subtitle {
        @include line-clamp(2);
    }

    // If list item text exceeds 3 lines reduce line clamp to 1 for all text
    // If list item text does not have a header & the title is 2 lines,
    // only reduce subtitle line clamp to 1
    .truncate-text .list-page-item-title,
    .truncate-text .list-page-item-subtitle,
    .truncate-subtitle {
        @include line-clamp(1);
    }

    .list-page-item-subtitle {
        color: var(--systemSecondary);
        font: var(--subhead);
    }
</style>
